import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import { mapActions, mapState } from "pinia";

import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import { useWindowStore } from "@vcl";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);


const ElementResizingMixinStaging = {
  data() {
    return {
      scaleFactor: 1,
      resizing: null,
      resizingStart: null,
      debounce: null,
      relatedElement: null,
    };
  },
  computed: {
    // Renamed from `activeState` to dodge a Vue prop/computed collision
    // — `OptionCalculationMixin` already declares an `activeState` PROP
    // (the per-element bp-qualified state passed down from the wrapper),
    // and a mapped state with the same name silently lost to the prop on
    // every read. The inspector store's `activeState` is the editor-wide
    // (state + activeBreakpoint) string, used here only by the resize
    // affordance to scope its option lookups.
    ...mapState(useDynamicComponentEditorStore, {
      inspectorActiveState: "activeState",
      gpuOverlayActive: "gpuOverlayActive",
    }),
    canResizeHorizontally() {
      const [state, breakpoint] = this.inspectorActiveState.split(":");
      const layoutWidth = DynamicElement.get(this.identifier)?.getRawOptionValue(
        "layout-width",
        breakpoint,
        state,
      )?.r;
      return this.editMode && typeof layoutWidth === "number";
    },
    canResizeVertically() {
      const [state, breakpoint] = this.inspectorActiveState.split(":");
      const layoutHeight = DynamicElement.get(this.identifier)?.getRawOptionValue(
        "layout-height",
        breakpoint,
        state,
      )?.r;
      return this.editMode && typeof layoutHeight === "number";
    },
  },
  beforeUnmount() {
    // Safety net: if the component unmounts mid-resize (drop, delete,
    // navigation), tear down any listeners we own. handleResizeStop is
    // idempotent — it no-ops when this.resizing is null.
    this.handleResizeStop();
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, [
      "findElementInLayout",
    ]),
    handleMouseDownResize(event, direction) {
      this.resizing = direction;
      this.relatedElement = this.dynamicComponentInstance.getElement(this.identifier);
      sessionStorage.setItem("resizing", this.identifier || "true");

      // Attach document listeners only while a resize is in flight. Previously
      // these were attached per-element in mounted(), which meant a canvas
      // with N elements ran N handlers on every mousemove — the root of the
      // builder's DnD/mousemove lag. See
      // docs/DYNAMIC_COMPONENT_EDITOR_PERFORMANCE.md.
      document.addEventListener("mousemove", this.handleResize);
      document.addEventListener("mouseup", this.handleResizeStop);

      const element = this.$el || event.target.closest(`[data-identifier="${this.identifier}"]`);
      this.scaleFactor = useWindowStore().calculateScaleFactor(element);

      this.originalWidth = this.getOptionValue({ optionKey: "layout-width" });
      this.originalHeight = this.getOptionValue({ optionKey: "layout-height" });
      this.startMouseX = event.clientX;
      this.startMouseY = event.clientY;

      if (["top", "bottom"].includes(direction)) {
        this.resizingStart = event.clientY;
      } else {
        this.resizingStart = event.clientX;
      }
    },

    handleResize(event) {
      // Listeners only attach while this element is actively resizing, so no
      // sessionStorage cross-element filter is needed.
      if (["top", "bottom"].includes(this.resizing)) {
        this.handleResizeVertically(event);
      } else {
        this.handleResizeHorizontally(event);
      }
    },

    handleResizeHorizontally(event) {
      const rawMouseDiff = event.clientX - this.startMouseX;
      const scaledMouseDiff = rawMouseDiff / this.scaleFactor;

      let newWidth;
      if (this.resizing === "left") {
        newWidth = this.originalWidth - scaledMouseDiff;
      } else {
        newWidth = this.originalWidth + scaledMouseDiff;
      }

      if (this.gpuOverlayActive) {
        newWidth = this.applyResizeSnap(newWidth, this.resizing);
      }

      if (newWidth > 0) {
        this.updateOptionConfigValue(
          LayoutWidth,
          Math.round(newWidth),
          `Element width value updated for ${this.dynamicComponentName}`,
        );
      }
    },

    handleResizeVertically(event) {
      const rawMouseDiff = event.clientY - this.startMouseY;
      const scaledMouseDiff = rawMouseDiff / this.scaleFactor;

      let newHeight;
      if (this.resizing === "top") {
        newHeight = this.originalHeight - scaledMouseDiff;
      } else {
        newHeight = this.originalHeight + scaledMouseDiff;
      }

      if (this.gpuOverlayActive) {
        newHeight = this.applyResizeSnap(newHeight, this.resizing);
      }

      if (newHeight > 0) {
        this.updateOptionConfigValue(
          LayoutHeight,
          Math.round(newHeight),
          `Element height value updated for ${this.dynamicComponentName}`,
        );
      }
    },
    updateOptionConfigValue(optionClass, value) {
      const [state, breakpoint] = this.inspectorActiveState.split(":");
      DynamicElement.get(this.identifier)?.updateOption(
        optionClass.optionKey,
        breakpoint,
        state,
        value,
      );
    },
    // Snap the resized edge to a sibling's / parent's edge or center within a small
    // screen-pixel threshold, and publish a guide line for the GPU overlay to draw.
    // Returns the (possibly snapped) value in element units. Only called when the GPU
    // overlay is active (flag on); the DOM resize path is otherwise unchanged.
    applyResizeSnap(value, direction) {
      const self = this.$el;
      const store = useDynamicComponentEditorStore();
      if (!self) {
        return value;
      }
      const selfRect = self.getBoundingClientRect();
      const sf = this.scaleFactor || 1;
      const horizontal = direction === "left" || direction === "right";
      const offset = value * sf;
      let movingPos;
      if (direction === "right") {
        movingPos = selfRect.left + offset;
      } else if (direction === "left") {
        movingPos = selfRect.right - offset;
      } else if (direction === "bottom") {
        movingPos = selfRect.top + offset;
      } else {
        movingPos = selfRect.bottom - offset;
      }

      const parent = self.parentElement;
      const candidates = [];
      if (parent) {
        const siblings = Array.from(parent.children).filter(
          c => c !== self && c.nodeType === 1 && c.hasAttribute?.("data-edit-identifier"),
        );
        for (const sib of siblings) {
          const r = sib.getBoundingClientRect();
          if (horizontal) {
            const halfW = r.width / 2;
            candidates.push(r.left, r.right, r.left + halfW);
            // Equal-width: the moving-edge position that makes self's width match this sibling's
            // (a flow-layout-appropriate "equal size" snap, since there's no free positioning).
            if (direction === "right") {
              candidates.push(selfRect.left + r.width);
            } else if (direction === "left") {
              candidates.push(selfRect.right - r.width);
            }
          } else {
            const halfH = r.height / 2;
            candidates.push(r.top, r.bottom, r.top + halfH);
            if (direction === "bottom") {
              candidates.push(selfRect.top + r.height);
            } else if (direction === "top") {
              candidates.push(selfRect.bottom - r.height);
            }
          }
        }
        const pr = parent.getBoundingClientRect();
        candidates.push(horizontal ? pr.left : pr.top, horizontal ? pr.right : pr.bottom);
      }

      let best = null;
      let bestDist = 6; // screen-px snap threshold
      for (const c of candidates) {
        const d = Math.abs(c - movingPos);
        if (d < bestDist) {
          bestDist = d;
          best = c;
        }
      }

      if (best === null) {
        if (store.activeSnapGuides.length) {
          store.activeSnapGuides = [];
        }
        return value;
      }

      const span = parent ? parent.getBoundingClientRect() : selfRect;
      store.activeSnapGuides = horizontal
        ? [{ x1: best, y1: span.top, x2: best, y2: span.bottom }]
        : [{ x1: span.left, y1: best, x2: span.right, y2: best }];

      let snapped;
      if (direction === "right") {
        snapped = (best - selfRect.left) / sf;
      } else if (direction === "left") {
        snapped = (selfRect.right - best) / sf;
      } else if (direction === "bottom") {
        snapped = (best - selfRect.top) / sf;
      } else {
        snapped = (selfRect.bottom - best) / sf;
      }
      return snapped > 0 ? snapped : value;
    },
    handleResizeStop() {
      if (!this.resizing) {
        return;
      }
      document.removeEventListener("mousemove", this.handleResize);
      document.removeEventListener("mouseup", this.handleResizeStop);
      sessionStorage.removeItem("resizing");
      const store = useDynamicComponentEditorStore();
      if (store.activeSnapGuides.length) {
        store.activeSnapGuides = [];
      }
      this.mouseOver = false;
      this.resizing = null;
      this.scaleFactor = 1;
      this.startMouseX = null;
      this.startMouseY = null;
      this.relatedElement = null;
    },
  },
};

export { ElementResizingMixinStaging };
